// 核心扫描引擎：二维码 + 条形码
// 三引擎策略：BarcodeDetector → jsQR → ZXing MultiFormatReader
import jsQR from 'jsqr';
import {
    MultiFormatReader,
    BarcodeFormat,
    DecodeHintType,
    RGBLuminanceSource,
    HybridBinarizer,
    BinaryBitmap
} from '@zxing/library';

export class ScannerEngine {
    constructor({ video, canvas, onResult, onStatus, onError } = {}) {
        this.video = video;
        this.canvas = canvas;
        this.ctx = canvas ? canvas.getContext('2d', { willReadFrequently: true }) : null;

        this.onResult = onResult || (() => {});
        this.onStatus = onStatus || (() => {});
        this.onError = onError || (() => {});

        this.stream = null;
        this.facingMode = 'environment';
        this.rafId = null;
        this.paused = true;
        this.track = null;
        this.torchOn = false;
        this.torchSupported = false;

        // === 多摄像头支持 ===
        this.cameras = [];           // [{deviceId,label,facingMode}]
        this.currentDeviceId = '';   // 当前使用的 deviceId

        this.roiRatio = 0.72;
        this.scanSize = 520;
        this._lastScanAt = 0;
        this._frameInterval = 60;

        // === 数字变焦（手指捏合缩放）===
        this.zoom = 1;
        this.minZoom = 1;
        this.maxZoom = 4;
        this.hardwareZoomSupported = false;
        this.hardwareZoomRange = null;

        this._initDetectors();
    }

    _initDetectors() {
        this.nativeDetector = null;
        if (typeof window !== 'undefined' && 'BarcodeDetector' in window) {
            try {
                this.nativeDetector = new window.BarcodeDetector({
                    formats: [
                        'qr_code', 'ean_13', 'ean_8', 'upc_a', 'upc_e',
                        'code_128', 'code_39', 'code_93', 'codabar',
                        'itf', 'pdf417', 'aztec', 'data_matrix'
                    ]
                });
            } catch {
                this.nativeDetector = null;
            }
        }

        try {
            const hints = new Map();
            hints.set(DecodeHintType.POSSIBLE_FORMATS, [
                BarcodeFormat.QR_CODE,
                BarcodeFormat.EAN_13,
                BarcodeFormat.EAN_8,
                BarcodeFormat.UPC_A,
                BarcodeFormat.UPC_E,
                BarcodeFormat.CODE_128,
                BarcodeFormat.CODE_39,
                BarcodeFormat.CODE_93,
                BarcodeFormat.CODABAR,
                BarcodeFormat.ITF,
                BarcodeFormat.PDF_417,
                BarcodeFormat.AZTEC,
                BarcodeFormat.DATA_MATRIX
            ]);
            hints.set(DecodeHintType.TRY_HARDER, true);
            const reader = new MultiFormatReader();
            reader.setHints(hints);
            this.zxingReader = reader;
        } catch {
            this.zxingReader = null;
        }
    }

    async start() {
        if (!navigator.mediaDevices?.getUserMedia) {
            throw new Error('当前浏览器不支持相机 API');
        }
        await this._openCamera({ facingMode: this.facingMode });
        // 国产手机（OPPO/小米等）首次 environment 可能拿到超广角/长焦，
        // 尝试挑选"主摄"并替换为主摄 deviceId
        await this._tryPickMainRearCamera();
        this.paused = false;
        this._loop();
    }

    /**
     * 尝试挑选后置主摄（排除超广角/长焦/微距/景深镜头）
     * 仅在当前不是明确的"主摄"时才执行切换
     */
    async _tryPickMainRearCamera() {
        try {
            // 刷新列表（此时已有权限，label 可读）
            const list = await this.listCameras(true);
            if (!list || list.length <= 1) return;

            // 过滤候选：仅保留后置
            const rear = list.filter((c) => {
                const l = (c.label || '').toLowerCase();
                // 明显的前置直接排除
                if (/front|user|facetime|自拍|前置/.test(l)) return false;
                return true;
            });
            if (rear.length <= 1) return;

            // 给每颗镜头评分，分数越高越像"主摄"
            const score = (cam) => {
                const l = (cam.label || '').toLowerCase();
                let s = 0;
                // 强烈正向：显式标注主摄
                if (/\bmain\b|主摄|1x|wide\s*camera|camera2\s*0/.test(l)) s += 100;
                if (/back|rear|后置|environment/.test(l)) s += 30;
                // 强烈负向：非主摄镜头
                if (/ultra[\s-]?wide|超广角|0\.5x|0\.6x/.test(l)) s -= 100;
                if (/tele|长焦|2x|3x|5x|10x/.test(l)) s -= 80;
                if (/macro|微距/.test(l)) s -= 80;
                if (/depth|景深|tof/.test(l)) s -= 120;
                if (/mono|黑白/.test(l)) s -= 60;
                // facingMode 提示
                if (cam.facingMode === 'environment') s += 10;
                return s;
            };

            const ranked = rear.map((c) => ({ cam: c, s: score(c) })).sort((a, b) => b.s - a.s);
            const best = ranked[0];
            const current = list.find((c) => c.deviceId === this.currentDeviceId);
            const currentScore = current ? score(current) : -999;

            // 只有找到明显更优的镜头时才切换，避免误切
            if (best && best.cam.deviceId && best.cam.deviceId !== this.currentDeviceId && best.s > currentScore + 20) {
                try {
                    await this._openCamera({ deviceId: best.cam.deviceId });
                } catch {
                    // 切换失败则继续用当前镜头，不阻塞
                }
            }
        } catch {
            // 静默失败，不影响主流程
        }
    }

    /**
     * 枚举所有可用摄像头（会在权限允许后返回 label）
     * @param {boolean} refresh 是否强制重新枚举
     */
    async listCameras(refresh = false) {
        if (!navigator.mediaDevices?.enumerateDevices) return [];
        if (this.cameras.length && !refresh) return this.cameras;
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const seen = new Set();
            const list = [];
            devices
                .filter((d) => d.kind === 'videoinput')
                .forEach((d, idx) => {
                    // 某些 iOS/Safari 会把同一物理摄像头返回多次（deviceId 为空串）
                    const key = d.deviceId || `anon_${idx}`;
                    if (seen.has(key)) return;
                    seen.add(key);
                    list.push({
                        deviceId: d.deviceId,
                        label: d.label || `摄像头 ${list.length + 1}`,
                        facingMode: guessFacing(d.label)
                    });
                });
            this.cameras = list;
            return this.cameras;
        } catch {
            return this.cameras;
        }
    }

    /**
     * 按 deviceId 打开指定摄像头
     */
    async openByDeviceId(deviceId) {
        if (!deviceId) return;
        this.pause();
        await this._openCamera({ deviceId });
        this.paused = false;
        this._loop();
    }

    async _openCamera({ facingMode, deviceId } = {}) {
        this._stopStream();

        // 构建候选 constraints 列表
        const constraints = [];
        if (deviceId) {
            constraints.push(
                { video: { deviceId: { exact: deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } }, audio: false },
                { video: { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
                { video: { deviceId: { exact: deviceId } }, audio: false }
            );
        } else {
            const fm = facingMode || this.facingMode;
            constraints.push(
                { video: { facingMode: { ideal: fm }, width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } }, audio: false },
                { video: { facingMode: { ideal: fm }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
                { video: { facingMode: { ideal: fm } }, audio: false },
                { video: true, audio: false }
            );
        }

        let lastErr;
        for (const c of constraints) {
            try {
                this.stream = await navigator.mediaDevices.getUserMedia(c);
                break;
            } catch (e) {
                lastErr = e;
            }
        }
        if (!this.stream) {
            if (lastErr && lastErr.name === 'NotAllowedError') throw new Error('相机权限被拒绝，请在浏览器设置中允许访问');
            if (lastErr && lastErr.name === 'NotFoundError') throw new Error('未检测到可用的摄像头');
            throw new Error('相机启动失败：' + (lastErr?.message || '未知错误'));
        }

        this.video.srcObject = this.stream;
        this.track = this.stream.getVideoTracks()[0];

        // 记录当前使用的 deviceId / facingMode（用于 UI 高亮）
        try {
            const settings = this.track.getSettings ? this.track.getSettings() : {};
            if (settings.deviceId) this.currentDeviceId = settings.deviceId;
            if (settings.facingMode) this.facingMode = settings.facingMode;
            else if (facingMode) this.facingMode = facingMode;
        } catch {
            if (facingMode) this.facingMode = facingMode;
        }

        if (this.facingMode === 'user') {
            this.video.classList.add('qrs-mirrored');
        } else {
            this.video.classList.remove('qrs-mirrored');
        }

        await new Promise((resolve) => {
            if (this.video.readyState >= 2) return resolve();
            this.video.onloadedmetadata = () => resolve();
        });
        try {
            await this.video.play();
        } catch {}

        await this._applyAdvancedConstraints();

        // 权限获取成功后再次枚举以拿到真实 label
        this.listCameras(true).catch(() => {});
    }

    async _applyAdvancedConstraints() {
        if (!this.track || !this.track.getCapabilities) return;
        try {
            const caps = this.track.getCapabilities() || {};
            this.torchSupported = !!caps.torch;

            // 探测硬件变焦能力（仅作能力记录：iOS Safari 等不支持 MediaStreamTrack 的
            // zoom 约束，因此缩放统一采用"数字变焦"实现，保证全平台一致）
            const z = caps.zoom;
            if (z && typeof z.min === 'number' && typeof z.max === 'number' && z.max > z.min) {
                this.hardwareZoomSupported = true;
                this.hardwareZoomRange = { min: z.min, max: z.max };
            } else {
                this.hardwareZoomSupported = false;
                this.hardwareZoomRange = null;
            }

            const advanced = [];
            if (caps.focusMode && caps.focusMode.includes('continuous')) advanced.push({ focusMode: 'continuous' });
            if (caps.exposureMode && caps.exposureMode.includes('continuous')) advanced.push({ exposureMode: 'continuous' });
            if (caps.whiteBalanceMode && caps.whiteBalanceMode.includes('continuous')) advanced.push({ whiteBalanceMode: 'continuous' });
            if (advanced.length) await this.track.applyConstraints({ advanced });
        } catch {}
    }

    _stopStream() {
        if (this.stream) {
            this.stream.getTracks().forEach((t) => t.stop());
            this.stream = null;
        }
        this.track = null;
        this.torchOn = false;
        this.torchSupported = false;
        this.hardwareZoomSupported = false;
        this.hardwareZoomRange = null;
        // 重开/切换摄像头后回到 1x，避免沿用上一颗镜头的倍率
        this.zoom = 1;
    }

    pause() {
        this.paused = true;
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }

    resume() {
        if (!this.stream) return;
        if (!this.paused) return;
        this.paused = false;
        this._loop();
    }

    stop() {
        this.pause();
        this._stopStream();
    }

    async switchCamera() {
        // 优先在多摄像头之间轮转；若仅前后两种，则切换 facingMode
        await this.listCameras().catch(() => {});
        if (this.cameras && this.cameras.length > 1) {
            // 排除非主摄的辅助镜头（景深/微距/黑白），它们对扫码无帮助
            const usable = this.cameras.filter((c) => {
                const l = (c.label || '').toLowerCase();
                return !/depth|景深|tof|macro|微距|mono|黑白/.test(l);
            });
            const pool = usable.length > 1 ? usable : this.cameras;
            const idx = pool.findIndex((c) => c.deviceId === this.currentDeviceId);
            const next = pool[(idx + 1) % pool.length];
            this._userSwitched = true; // 标记为用户手动切换
            return this.openByDeviceId(next.deviceId);
        }
        this.facingMode = this.facingMode === 'environment' ? 'user' : 'environment';
        this._userSwitched = true;
        this.pause();
        await this._openCamera({ facingMode: this.facingMode });
        this.paused = false;
        this._loop();
    }

    async toggleTorch() {
        if (!this.track) throw new Error('相机未启动');
        if (!this.torchSupported) throw new Error('此设备/浏览器不支持手电筒');
        const next = !this.torchOn;
        await this.track.applyConstraints({ advanced: [{ torch: next }] });
        this.torchOn = next;
        return next;
    }

    /**
     * 设置变焦倍率（数字变焦）
     * 视觉放大由 QrScanner 对 video 做 CSS 缩放完成，这里只负责让"解码采样区域"
     * 同步收缩到画面中心，从而保证「看到的」与「识别的」一致（否则放大了也扫不到）
     * @param {number} value 目标倍率，超出范围会被夹取
     * @returns {number} 实际生效的倍率
     */
    setZoom(value) {
        const v = Number(value);
        const safe = isFinite(v) && v > 0 ? v : this.minZoom;
        this.zoom = Math.min(this.maxZoom, Math.max(this.minZoom, safe));
        return this.zoom;
    }

    getZoom() {
        return this.zoom;
    }

    _loop() {
        if (this.paused) return;
        const now = performance.now();
        if (now - this._lastScanAt >= this._frameInterval) {
            this._lastScanAt = now;
            const vw = this.video.videoWidth;
            const vh = this.video.videoHeight;
            if (vw && vh) {
                this._scanFrameAsync(vw, vh)
                    .then((res) => {
                        if (res && !this.paused) {
                            this.onResult({ data: res.text, format: res.format, source: 'camera' });
                        }
                    })
                    .catch(() => {});
            }
        }
        this.rafId = requestAnimationFrame(() => this._loop());
    }

    async _scanFrameAsync(vw, vh) {
        const baseShort = Math.min(vw, vh);
        const zoom = this.zoom > 1 ? this.zoom : 1;

        // 数字变焦：倍率越高，中心采样区域越小（等效于相机变焦）。
        // 采样区域始终以画面中心对齐，与 QrScanner 的 CSS 缩放保持视觉一致。
        // zoom === 1 时与原实现完全等价。
        const sideSq = Math.max(2, Math.floor((baseShort * this.roiRatio) / zoom));
        const sxSq = Math.floor((vw - sideSq) / 2);
        const sySq = Math.floor((vh - sideSq) / 2);

        const wideW = Math.max(2, Math.floor((baseShort * 0.9) / zoom));
        const wideH = Math.max(2, Math.floor((baseShort * 0.45) / zoom));
        const sxWide = Math.floor((vw - wideW) / 2);
        const syWide = Math.floor((vh - wideH) / 2);

        let res = await this._detectRegion(sxSq, sySq, sideSq, sideSq, this.scanSize, this.scanSize);
        if (res) return res;

        // 输出分辨率保持基准值（不随变焦下降），保证放大后仍有足够细节供解码
        const targetW = Math.min(Math.floor(baseShort * 0.9), 640);
        const targetH = Math.max(2, Math.round(targetW * (wideH / wideW)));
        res = await this._detectRegion(sxWide, syWide, wideW, wideH, targetW, targetH);
        return res;
    }

    async _detectRegion(sx, sy, sw, sh, tw, th) {
        this.canvas.width = tw;
        this.canvas.height = th;

        if (this.facingMode === 'user') {
            this.ctx.save();
            this.ctx.translate(tw, 0);
            this.ctx.scale(-1, 1);
            this.ctx.drawImage(this.video, sx, sy, sw, sh, 0, 0, tw, th);
            this.ctx.restore();
        } else {
            this.ctx.drawImage(this.video, sx, sy, sw, sh, 0, 0, tw, th);
        }

        if (this.nativeDetector) {
            try {
                const barcodes = await this.nativeDetector.detect(this.canvas);
                if (barcodes && barcodes.length) {
                    const b = barcodes[0];
                    return { text: b.rawValue, format: normalizeNativeFormat(b.format) };
                }
            } catch {}
        }

        const imgData = this.ctx.getImageData(0, 0, tw, th);

        try {
            const qr = jsQR(imgData.data, imgData.width, imgData.height, { inversionAttempts: 'dontInvert' });
            if (qr && qr.data) return { text: qr.data, format: 'QR Code' };
        } catch {}

        if (this.zxingReader) {
            const out = this._decodeWithZXing(imgData);
            if (out) return out;
        }
        return null;
    }

    _decodeWithZXing(imgData) {
        try {
            const luminance = new RGBLuminanceSource(
                toLuminanceArray(imgData),
                imgData.width,
                imgData.height
            );
            const binarizer = new HybridBinarizer(luminance);
            const bitmap = new BinaryBitmap(binarizer);
            const result = this.zxingReader.decode(bitmap);
            if (result && result.getText) {
                this.zxingReader.reset();
                return { text: result.getText(), format: zxingFormatName(result.getBarcodeFormat()) };
            }
        } catch {
            try { this.zxingReader.reset(); } catch {}
        }
        return null;
    }

    // ========= 相册图片解码 =========
    async decodeFromImage(file) {
        const img = await loadImage(file);

        if (this.nativeDetector) {
            try {
                const barcodes = await this.nativeDetector.detect(img);
                if (barcodes && barcodes.length) {
                    return { text: barcodes[0].rawValue, format: normalizeNativeFormat(barcodes[0].format) };
                }
            } catch {}
        }

        const maxDim = Math.max(img.width, img.height);
        const sizes = [maxDim, 1600, 1200, 1000, 800, 600, 400]
            .filter((s, i, arr) => s > 200 && arr.indexOf(s) === i)
            .sort((a, b) => b - a);

        for (const size of sizes) {
            const data = drawImageToCanvas(img, size);

            try {
                const code = jsQR(data.data, data.width, data.height, { inversionAttempts: 'attemptBoth' });
                if (code && code.data) return { text: code.data, format: 'QR Code' };
            } catch {}

            if (this.zxingReader) {
                const r = this._decodeWithZXing(data);
                if (r) return r;
            }

            const enhanced = enhanceContrast(data);
            if (this.zxingReader) {
                const r = this._decodeWithZXing(enhanced);
                if (r) return r;
            }
            try {
                const q = jsQR(enhanced.data, enhanced.width, enhanced.height, { inversionAttempts: 'attemptBoth' });
                if (q && q.data) return { text: q.data, format: 'QR Code' };
            } catch {}
        }

        for (const size of [1200, 800, 600]) {
            const cropped = drawImageCrop(img, size, 0.7);
            try {
                const code = jsQR(cropped.data, cropped.width, cropped.height, { inversionAttempts: 'attemptBoth' });
                if (code && code.data) return { text: code.data, format: 'QR Code' };
            } catch {}
            if (this.zxingReader) {
                const r = this._decodeWithZXing(cropped);
                if (r) return r;
            }
        }

        return null;
    }
}

// ========== 纯函数辅助 ==========
function guessFacing(label = '') {
    const l = label.toLowerCase();
    if (/front|user|facetime|自拍|前置/.test(l)) return 'user';
    if (/back|rear|environment|后置/.test(l)) return 'environment';
    return '';
}

function toLuminanceArray(imgData) {
    const d = imgData.data;
    const n = d.length / 4;
    const out = new Int32Array(n);
    for (let i = 0, j = 0; i < n; i++, j += 4) {
        out[i] = (d[j] << 16) | (d[j + 1] << 8) | d[j + 2];
    }
    return out;
}

function zxingFormatName(fmt) {
    const names = {
        [BarcodeFormat.QR_CODE]: 'QR Code',
        [BarcodeFormat.EAN_13]: 'EAN-13',
        [BarcodeFormat.EAN_8]: 'EAN-8',
        [BarcodeFormat.UPC_A]: 'UPC-A',
        [BarcodeFormat.UPC_E]: 'UPC-E',
        [BarcodeFormat.CODE_128]: 'Code 128',
        [BarcodeFormat.CODE_39]: 'Code 39',
        [BarcodeFormat.CODE_93]: 'Code 93',
        [BarcodeFormat.CODABAR]: 'Codabar',
        [BarcodeFormat.ITF]: 'ITF',
        [BarcodeFormat.PDF_417]: 'PDF417',
        [BarcodeFormat.AZTEC]: 'Aztec',
        [BarcodeFormat.DATA_MATRIX]: 'Data Matrix'
    };
    return names[fmt] || String(fmt);
}

function normalizeNativeFormat(nativeFmt) {
    const map = {
        qr_code: 'QR Code',
        ean_13: 'EAN-13',
        ean_8: 'EAN-8',
        upc_a: 'UPC-A',
        upc_e: 'UPC-E',
        code_128: 'Code 128',
        code_39: 'Code 39',
        code_93: 'Code 93',
        codabar: 'Codabar',
        itf: 'ITF',
        pdf417: 'PDF417',
        aztec: 'Aztec',
        data_matrix: 'Data Matrix'
    };
    return map[nativeFmt] || nativeFmt || '未知';
}

function loadImage(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('图片加载失败'));
        };
        img.src = url;
    });
}

function drawImageToCanvas(img, maxSide) {
    const ratio = img.width / img.height;
    let w;
    let h;
    if (img.width > img.height) {
        w = Math.min(maxSide, img.width);
        h = Math.round(w / ratio);
    } else {
        h = Math.min(maxSide, img.height);
        w = Math.round(h * ratio);
    }
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const cx = c.getContext('2d', { willReadFrequently: true });
    cx.drawImage(img, 0, 0, w, h);
    return cx.getImageData(0, 0, w, h);
}

function drawImageCrop(img, maxSide, ratio) {
    const side = Math.min(img.width, img.height) * ratio;
    const sx = (img.width - side) / 2;
    const sy = (img.height - side) / 2;
    const c = document.createElement('canvas');
    c.width = maxSide;
    c.height = maxSide;
    const cx = c.getContext('2d', { willReadFrequently: true });
    cx.drawImage(img, sx, sy, side, side, 0, 0, maxSide, maxSide);
    return cx.getImageData(0, 0, maxSide, maxSide);
}

function enhanceContrast(imgData) {
    const data = imgData.data;
    const len = data.length;
    const histo = new Uint32Array(256);
    const totalPixels = len / 4;
    for (let i = 0; i < len; i += 4) {
        const gray = ((data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000) | 0;
        histo[gray]++;
    }
    const cut = totalPixels * 0.01;
    let lo = 0;
    let hi = 255;
    let acc = 0;
    for (let i = 0; i < 256; i++) {
        acc += histo[i];
        if (acc > cut) {
            lo = i;
            break;
        }
    }
    acc = 0;
    for (let i = 255; i >= 0; i--) {
        acc += histo[i];
        if (acc > cut) {
            hi = i;
            break;
        }
    }
    if (hi <= lo) return imgData;
    const range = hi - lo;
    const out = new Uint8ClampedArray(len);
    for (let i = 0; i < len; i += 4) {
        const gray = (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
        let v = ((gray - lo) / range) * 255;
        if (v < 0) v = 0;
        else if (v > 255) v = 255;
        out[i] = out[i + 1] = out[i + 2] = v;
        out[i + 3] = 255;
    }
    return new ImageData(out, imgData.width, imgData.height);
}